/* External */
import {
  useMemo, memo, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import CommonCollectionCard from '@dtvgo/collection-card';

/* Utils */
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { isLanderCollection } from '../../utils/metaDataUtils';
import { LANDER_LOGO_BASE_IMAGE_URL, ImageRatio } from '../../utils/constants';
import { CardType, CardWidth } from '../../utils/cardsUtils';

/* Hooks */
import useImageCard from '../../hooks/useImageCard';

/* Styles */
import './CollectionCard.scss';

const IMG_WIDTH = CardWidth[CardType.COLLECTION] + 20;
const IMG_DIMENSION = ImageRatio.ONE_BY_ONE;

function CollectionCard({
  className,
  item: collection,
  focused,
  onMouseDown,
  onMouseEnter,
}) {
  const { title, cardImages } = collection;
  const [isErrorLanderLogo, setIsErrorLanderLogo] = useState(false);
  const isLander = useMemo(() => isLanderCollection(collection), [collection]);
  const { imageUrl } = useImageCard({
    images: cardImages,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const handleLanderLogoError = useCallback(() => {
    setIsErrorLanderLogo(true);
  }, []);

  useEffect(() => {
    setIsErrorLanderLogo(false);
  }, []);

  return (
    <CommonCollectionCard
      className={`dtv-html5-c-collection-card ${
        focused ? 'dtv-html5-c-collection-card--focused' : ''
      } ${className}`}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <CommonCollectionCard.Image imageUrl={imageUrl} imageAlt={title} />
      {!isLander || isErrorLanderLogo ? (
        <CommonCollectionCard.Title className="dtv-html5-c-collection-card__title">
          {title || ''}
        </CommonCollectionCard.Title>
      ) : (
        <CommonCollectionCard.LanderLogo
          className="dtv-html5-c-collection-card__lander-logo"
          title={title}
          baseImageUrl={LANDER_LOGO_BASE_IMAGE_URL}
          onError={handleLanderLogoError}
        />
      )}
    </CommonCollectionCard>
  );
}

CollectionCard.propTypes = {
  className: PropTypes.string,
  item: PropTypes.shape().isRequired,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

CollectionCard.defaultProps = {
  className: '',
  onMouseDown: undefined,
  onMouseEnter: undefined,
};

export default memo(withInteractiveFocusable()(memo(CollectionCard)));
